import React, { type ReactEventHandler } from 'react';

import { North } from '@mui/icons-material';
import { Skeleton, styled } from '@mui/material';
import UserAvatar from 'Components/UserAvatar';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useHookForm } from '../../hooks/useHookForm';
import { getDefaultAvatarUrl } from '..';

const StyledAlignedEndInput = styled(TextField)(() => ({
    '#vanity-url': {
        textAlign: 'end',
    },
}));

const StyledWidthWrapper = styled('div')<{ $width?: string }>(({ $width }) => ({
    width: $width || '60%',
}));

const CenterContentContainer = styled('div')({
    alignItems: 'center',
    justifyContent: 'center',
    display: 'flex',
    gap: '10px',
    flexDirection: 'column',
});

const formInputs = {
    firstName: 'userGivenName',
    lastName: 'userSurname',
    displayName: 'displayName',
    pronouns: 'pronouns',
    email: 'email',
};

export interface UserProps {
    firstName?: string;
    lastName?: string;
    displayName?: string;
    avatarUrl?: string;
    pronouns?: string;
    email?: string;
}

interface FormProps {
    showLabel?: boolean;
    editProfilePicture?: boolean;
    handleFileChange?: ReactEventHandler;
    onFormStateChanged: React.Dispatch<React.SetStateAction<UserProps>>;
    currentState: UserProps;
    fileUrl?: string;
    fileUploadError?: string;
}

const MAX_PROFILE_PICTURE_FILE_SIZE_IN_BYTES = Number(
    process.env.OPERATOR_MAX_PROFILE_PICTURE_FILE_SIZE_IN_BYTES,
);
const MAX_PROFILE_PICTURE_FILE_SIZE_IN_MB =
    MAX_PROFILE_PICTURE_FILE_SIZE_IN_BYTES / 1024;

const SkeletonForm = () => {
    return (
        <>
            <CenterContentContainer>
                <Skeleton
                    variant="circular"
                    sx={{
                        display: 'flex',
                        backgroundSize: '160px 160px',
                        height: '160px',
                        width: '160px',
                        margin: '0px auto',
                    }}
                />
                <North
                    style={{
                        alignSelf: 'center',
                        color: '#999999',
                        margin: '0 0 0 0',
                    }}
                />

                <Typography
                    color="grey"
                    margin="0 0 0 0"
                    style={{ color: '#999999' }}
                    variant="body2"
                    align="center"
                >
                    Wow, you look great
                </Typography>
            </CenterContentContainer>
            <br />
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '24px',
                }}
            >
                <Skeleton
                    variant="rectangular"
                    sx={{
                        width: '100%',
                        margin: '5px 0px 30px',
                        color: 'grey',
                        height: '40px',
                    }}
                />
                <Skeleton
                    variant="rectangular"
                    sx={{
                        width: '100%',
                        margin: '5px 0px 30px',
                        color: 'grey',
                        height: '40px',
                    }}
                />
            </div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '24px',
                }}
            >
                <Skeleton
                    variant="rectangular"
                    sx={{
                        width: '100%',
                        margin: '5px 0px 30px',
                        color: 'grey',
                        height: '40px',
                    }}
                />
                <Skeleton
                    variant="rectangular"
                    sx={{
                        width: '100%',
                        margin: '5px 0px 30px',
                        color: 'grey',
                        height: '40px',
                    }}
                />
            </div>
            <Skeleton
                variant="rectangular"
                sx={{
                    width: '100%',
                    margin: '0px 0px 30px',
                    color: 'grey',
                    height: '40px',
                }}
            />
        </>
    );
};

const BasicForm = ({
    showLabel,
    editProfilePicture,
    handleFileChange,
    onFormStateChanged,
    currentState,
    fileUploadError,
    fileUrl,
}: FormProps) => {
    const { register, formState } = useHookForm();

    const avatar = getDefaultAvatarUrl(currentState);

    // This helps the Avatar component prevent on loading
    // cached results from the browser.
    const imageURLRandomKey = Math.floor(Math.random() * 1000);

    return (
        <StyledWidthWrapper
            $width="100%"
            style={{
                backgroundColor: 'white',
                boxShadow: 'rgba(0, 0, 0, 0.15) 0px 2px 4px 0px',
            }}
        >
            <Container>
                {!currentState.email ? (
                    <SkeletonForm />
                ) : (
                    <>
                        {editProfilePicture ? (
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '0px',
                                }}
                            >
                                <div style={{ padding: '20px 0' }}>
                                    <UserAvatar
                                        color="white"
                                        style={{
                                            width: '80px',
                                            height: '80px',
                                            fontSize: 'xx-large'
                                        }}
                                        src={
                                            fileUrl ||
                                            (avatar?.isUrl
                                                ? `${avatar.avatar}?${imageURLRandomKey}`
                                                : '')
                                        }
                                        alt={currentState.displayName || `${currentState.firstName} ${currentState.lastName}`}
                                    />
                                </div>

                                {/* Disabling file upload, reimplementation at a later stage */}
                                {/* <FileUploader
                                    label={'Change Avatar'}
                                    subLabel={`PNG/JPG, ${MAX_PROFILE_PICTURE_FILE_SIZE_IN_MB}}mb Max`}
                                    containerStyle={{
                                        width: '100%',
                                        paddingRight: 0,
                                    }}
                                    accept="image/png, image/jpeg"
                                    multiple={false}
                                    error={fileUploadError}
                                    onChange={(e) => {
                                        if (handleFileChange) {
                                            handleFileChange(e);
                                        }
                                    }}
                                /> */}
                            </div>
                        ) : (
                            <CenterContentContainer>
                                <UserAvatar
                                    src={
                                        avatar?.isUrl
                                            ? `${avatar.avatar}?${imageURLRandomKey}`
                                            : ''
                                    }
                                    alt={currentState.displayName || `${currentState.firstName} ${currentState.lastName}`}
                                    style={{
                                        width: '160px',
                                        height: '160px',
                                        fontSize: 'xxx-large'
                                    }}
                                />

                                <North
                                    style={{
                                        alignSelf: 'center',
                                        color: '#999999',
                                        margin: '0 0 0 0',
                                    }}
                                />

                                <Typography
                                    color="grey"
                                    margin="0 0 0 0"
                                    style={{ color: '#999999' }}
                                    variant="body2"
                                    align="center"
                                >
                                    Don't they look good?
                                </Typography>
                            </CenterContentContainer>
                        )}
                        <br />
                        <form
                            onSubmit={(event) => {
                                event.preventDefault();
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    gap: '24px',
                                }}
                            >
                                <TextField
                                    {...register(formInputs.firstName, {
                                        required: 'Given Name is required.',
                                        onChange: (e) => {
                                            onFormStateChanged({
                                                ...currentState,
                                                firstName: e.target.value,
                                            });
                                        },
                                    })}
                                    value={currentState?.firstName}
                                    style={{
                                        color: 'grey',
                                        width: '100%',
                                        margin: '5px 0px 30px',
                                    }}
                                    placeholder=""
                                    title={'Given Name'}
                                    label={'Given Name'}
                                    error={Boolean(
                                        formState.errors.userGivenName?.message,
                                    )}
                                    helperText={
                                        currentState?.firstName
                                            ? (formState.errors.userGivenName
                                                  ?.message as unknown as string)
                                            : 'Given Name is required.'
                                    }
                                />
                                <TextField
                                    {...register(formInputs.lastName, {
                                        required: 'Surname is required.',
                                        onChange: (e) => {
                                            onFormStateChanged({
                                                ...currentState,
                                                lastName: e.target.value,
                                            });
                                        },
                                    })}
                                    value={currentState?.lastName}
                                    style={{
                                        color: 'grey',
                                        width: '100%',
                                        margin: '5px 0px 30px',
                                    }}
                                    error={Boolean(
                                        !currentState?.lastName ||
                                            formState.errors.userSurname
                                                ?.message,
                                    )}
                                    helperText={
                                        currentState?.lastName
                                            ? (formState.errors.userSurname
                                                  ?.message as unknown as string)
                                            : 'Surname is required.'
                                    }
                                    label={'Surname'}
                                    title={'Surname'}
                                />
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    gap: '24px',
                                }}
                            >
                                <TextField
                                    {...register(formInputs.displayName, {
                                        onChange: (e) => {
                                            onFormStateChanged({
                                                ...currentState,
                                                displayName: e.target.value,
                                            });
                                        },
                                    })}
                                    value={currentState?.displayName}
                                    style={{
                                        color: 'grey',
                                        width: '100%',
                                        margin: '5px 0px 30px',
                                    }}
                                    placeholder=""
                                    title={'Display Name'}
                                    label={'Display Name'}
                                />
                                <TextField
                                    {...register(formInputs.pronouns, {
                                        onChange: (e) => {
                                            onFormStateChanged({
                                                ...currentState,
                                                pronouns: e.target.value,
                                            });
                                        },
                                    })}
                                    value={currentState?.pronouns}
                                    style={{
                                        color: 'grey',
                                        width: '100%',
                                        margin: '5px 0px 30px',
                                    }}
                                    placeholder=""
                                    label={'Pronouns'}
                                    title={'Pronouns'}
                                />
                            </div>
                        </form>
                        <StyledAlignedEndInput
                            value={currentState?.email}
                            disabled
                            style={{
                                color: 'grey',
                                width: '100%',
                                margin: '0 0 30px',
                            }}
                            label={'Email'}
                            id="user-email"
                            title={'Email'}
                            helperText={
                                showLabel
                                    ? 'Quality doesn’t allow you to change your email address'
                                    : ''
                            }
                        />
                    </>
                )}
            </Container>
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px',
                }}
            ></div>
        </StyledWidthWrapper>
    );
};

export default BasicForm;
